import { showUserAlert } from "../utils/userErrorMessage";
import React, { useEffect, useMemo, useState } from "react";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Button } from "../components/ui/button";

const salaryTypes = {
  driver: {
    label: "기사",
    role: "driver",
    personnel: ["hire_date", "driver_name", "car_number"],
    payment: ["base_salary", "service_allowance", "extra_overtime_allowance", "allowance"],
    deduction: [
      "national_pension",
      "health_insurance",
      "care_insurance",
      "employment_insurance",
      "income_tax",
      "local_income_tax",
      "advance_payment",
    ],
  },
  office: {
    label: "사무실",
    role: "office",
    personnel: ["hire_date", "driver_name"],
    payment: ["base_salary", "service_allowance", "meal_allowance"],
    deduction: [
      "national_pension",
      "health_insurance",
      "care_insurance",
      "employment_insurance",
      "income_tax",
      "local_income_tax",
    ],
  },
  daily: {
    label: "일용직",
    role: "daily",
    personnel: ["driver_name", "resident_number"],
    payment: ["daily_wage", "work_days"],
    deduction: [
      "national_pension",
      "health_insurance",
      "care_insurance",
      "employment_insurance",
      "income_tax",
      "local_income_tax",
      "year_end_adjustment",
    ],
  },
};

const fieldLabels = {
  sequence: "순번",
  hire_date: "입사일",
  driver_name: "성명",
  car_number: "차량번호",
  resident_number: "주민번호",
  base_salary: "기본급",
  service_allowance: "근속수당",
  extra_overtime_allowance: "시간외추가수당",
  allowance: "수당",
  meal_allowance: "식대",
  daily_wage: "일당",
  work_days: "근속일",
  payment_total: "지급합계",
  national_pension: "국민연금",
  health_insurance: "건강보험",
  care_insurance: "장기요양보험",
  employment_insurance: "고용보험",
  income_tax: "소득세",
  local_income_tax: "지방소득세",
  advance_payment: "가불",
  advance_payment_note: "가불 메모",
  year_end_adjustment: "연말정산",
  deduction_total: "공제합계",
  net_pay: "차인지급액",
  salary_pay_day: "지급일",
  salary_pay_date: "지급일",
};

const moneyInputFields = new Set([
  "base_salary",
  "service_allowance",
  "extra_overtime_allowance",
  "allowance",
  "meal_allowance",
  "daily_wage",
  "work_days",
  "national_pension",
  "health_insurance",
  "care_insurance",
  "income_tax",
  "advance_payment",
  "year_end_adjustment",
]);

const getMonthStart = (value) => {
  if (value && /^\d{4}-\d{2}/.test(value)) return `${value.slice(0, 7)}-01`;
  const now = new Date();
  return `${now.toISOString().slice(0, 7)}-01`;
};

const toMonthInput = (date) => (date || "").slice(0, 7);

const getPreviousMonthStart = (date) => {
  const [year, month] = getMonthStart(date).split("-").map(Number);
  const previous = new Date(year, month - 2, 1);
  return `${previous.getFullYear()}-${String(previous.getMonth() + 1).padStart(2, "0")}-01`;
};

const getSalaryExpenseDate = (month, payDate, payDay) => {
  if (payDate && /^\d{4}-\d{2}-\d{2}$/.test(payDate)) return payDate;
  const base = getMonthStart(month);
  const [year, monthNumber] = base.split("-").map(Number);
  const lastDay = new Date(year, monthNumber, 0).getDate();
  const day = Math.min(Math.max(toNumber(payDay) || lastDay, 1), lastDay);
  return `${base.slice(0, 8)}${String(day).padStart(2, "0")}`;
};

const getMonthEnd = (month) => {
  const base = getMonthStart(month);
  const [year, monthNumber] = base.split("-").map(Number);
  const lastDay = new Date(year, monthNumber, 0).getDate();
  return `${base.slice(0, 8)}${String(lastDay).padStart(2, "0")}`;
};

const getToday = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const getCurrentTime = () => {
  const date = new Date();
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value ?? "").replace(/[^0-9-]/g, "");
  if (!cleaned || cleaned === "-") return 0;
  return Number(cleaned) || 0;
};

const formatMoney = (value) => toNumber(value).toLocaleString();
const floorToTen = (value) => Math.floor(toNumber(value) / 10) * 10;
const roundToTen = (value) => Math.round(toNumber(value) / 10) * 10;
const normalizeTextKey = (value) => String(value || "").trim().replace(/\s+/g, " ");
const normalizePhoneKey = (value) => String(value || "").replace(/\D/g, "");
const normalizeEmailKey = (value) => String(value || "").trim().toLowerCase();
const SALARY_ROW_EXCLUSIONS_TABLE = "salary_row_exclusions";
const SALARY_APP_MESSAGE_OUTBOX_TABLE = "salary_app_message_outbox";

const DetailField = ({ label, value, align = "left" }) => (
  <div className="flex items-center justify-between gap-3 border-b py-1">
    <span className="text-gray-600">{label}</span>
    <span className={align === "right" ? "text-right font-medium" : "font-medium"}>{value || "-"}</span>
  </div>
);

const makeEmptySalary = (person, month, company, companyName, salaryType) => ({
  id: null,
  profile_id: person.id || null,
  salary_type: salaryType,
  role: person.role || salaryTypes[salaryType].role,
  company,
  company_name: companyName || null,
  driver_name: person.driver_name || "",
  driver_email: person.email || null,
  phone: person.phone || null,
  car_number: person.car_number || "",
  hire_date: person.hire_date || "",
  resident_number: person.resident_number || "",
  salary_pay_day: person.salary_pay_day || "",
  salary_pay_date: "",
  month,
  salary: 0,
  base_salary: 0,
  service_allowance: 0,
  extra_overtime_allowance: 0,
  allowance: 0,
  meal_allowance: 0,
  daily_wage: 0,
  work_days: 0,
  national_pension: 0,
  health_insurance: 0,
  care_insurance: 0,
  employment_insurance: 0,
  income_tax: 0,
  local_income_tax: 0,
  advance_payment: 0,
  advance_payment_note: "",
  year_end_adjustment: 0,
  other_deduction: 0,
  payment_total: 0,
  deduction_total: 0,
  net_pay: 0,
  national_pension_exempt: false,
  employment_insurance_exempt: false,
});

const normalizeSalaryRow = (person, salary, month, company, companyName, salaryType) => {
  const base = makeEmptySalary(person, month, company, companyName, salaryType);
  if (!salary) return base;

  return {
    ...base,
    ...salary,
    profile_id: person.id || salary.profile_id || null,
    salary_type: salary.salary_type || salaryType,
    car_number: person.car_number || salary.car_number || "",
    hire_date: person.hire_date || "",
    resident_number: person.resident_number || salary.resident_number || "",
    salary_pay_day: person.salary_pay_day || salary.salary_pay_day || "",
    salary_pay_date: salary.salary_pay_date || "",
    company_name: salary.company_name || companyName || null,
    base_salary: toNumber(salary.base_salary),
    service_allowance: toNumber(salary.service_allowance),
    extra_overtime_allowance: toNumber(salary.extra_overtime_allowance),
    allowance: toNumber(salary.allowance ?? salary.overtime_allowance),
    meal_allowance: toNumber(salary.meal_allowance),
    daily_wage: toNumber(salary.daily_wage),
    work_days: toNumber(salary.work_days),
    national_pension: toNumber(salary.national_pension),
    health_insurance: toNumber(salary.health_insurance),
    care_insurance: toNumber(salary.care_insurance),
    employment_insurance: toNumber(salary.employment_insurance),
    income_tax: toNumber(salary.income_tax),
    local_income_tax: toNumber(salary.local_income_tax),
    advance_payment: toNumber(salary.advance_payment ?? salary.other_deduction),
    advance_payment_note: salary.advance_payment_note || "",
    year_end_adjustment: toNumber(salary.year_end_adjustment),
    national_pension_exempt: Boolean(salary.national_pension_exempt),
    employment_insurance_exempt: Boolean(salary.employment_insurance_exempt),
  };
};

const calculateRow = (row) => {
  const salaryType = row.salary_type || "driver";
  const paymentTotal =
    salaryType === "daily"
      ? toNumber(row.daily_wage) * toNumber(row.work_days)
      : salaryType === "office"
        ? toNumber(row.base_salary) + toNumber(row.service_allowance) + toNumber(row.meal_allowance)
        : toNumber(row.base_salary) +
          toNumber(row.service_allowance) +
          toNumber(row.extra_overtime_allowance) +
          toNumber(row.allowance);

  const employmentInsurance = row.employment_insurance_exempt
    ? 0
    : floorToTen(paymentTotal * 0.009);
  const localIncomeTax = roundToTen(toNumber(row.income_tax) * 0.1);
  const nationalPension = row.national_pension_exempt ? 0 : toNumber(row.national_pension);
  const extraDeduction =
    salaryType === "daily" ? toNumber(row.year_end_adjustment) : toNumber(row.advance_payment);

  const deductionTotal =
    nationalPension +
    toNumber(row.health_insurance) +
    toNumber(row.care_insurance) +
    employmentInsurance +
    toNumber(row.income_tax) +
    localIncomeTax +
    extraDeduction;

  return {
    ...row,
    national_pension: nationalPension,
    employment_insurance: employmentInsurance,
    local_income_tax: localIncomeTax,
    payment_total: paymentTotal,
    deduction_total: deductionTotal,
    net_pay: paymentTotal - deductionTotal,
    salary: paymentTotal - deductionTotal,
    other_deduction: salaryType === "daily" ? 0 : toNumber(row.advance_payment),
    overtime_allowance: toNumber(row.allowance),
  };
};

const isSameSalaryPerson = (left = {}, right = {}) => {
  const leftProfileId = left.profile_id || left.id || "";
  const rightProfileId = right.profile_id || right.id || "";
  if (leftProfileId && rightProfileId && leftProfileId === rightProfileId) return true;

  const leftPhone = normalizePhoneKey(left.phone);
  const rightPhone = normalizePhoneKey(right.phone);
  if (leftPhone && rightPhone && leftPhone === rightPhone) return true;

  const leftEmail = normalizeEmailKey(left.driver_email || left.email);
  const rightEmail = normalizeEmailKey(right.driver_email || right.email);
  if (leftEmail && rightEmail && leftEmail === rightEmail) return true;

  const leftResidentNumber = normalizeTextKey(left.resident_number);
  const rightResidentNumber = normalizeTextKey(right.resident_number);
  if (leftResidentNumber && rightResidentNumber && leftResidentNumber === rightResidentNumber) return true;

  const leftName = normalizeTextKey(left.driver_name || left.name);
  const rightName = normalizeTextKey(right.driver_name || right.name);
  const leftCarNumber = normalizeTextKey(left.car_number);
  const rightCarNumber = normalizeTextKey(right.car_number);
  return !!leftName && !!rightName && leftName === rightName && leftCarNumber === rightCarNumber;
};

const getSalaryPersonKey = (row = {}) => {
  const profileId = row.profile_id || "";
  if (profileId) return `id:${profileId}`;

  const phone = normalizePhoneKey(row.phone);
  if (phone) return `phone:${phone}`;

  const email = normalizeEmailKey(row.driver_email || row.email);
  if (email) return `email:${email}`;

  const residentNumber = normalizeTextKey(row.resident_number);
  if (residentNumber) return `resident:${residentNumber}`;

  const name = normalizeTextKey(row.driver_name || row.name);
  const carNumber = normalizeTextKey(row.car_number);
  if (name || carNumber) return `name:${name}__car:${carNumber}`;

  return "";
};

const isMissingSalaryExclusionsTable = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""} ${error?.hint || ""}`;
  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    message.includes(SALARY_ROW_EXCLUSIONS_TABLE)
  );
};

const isMissingSalaryAppMessageOutboxTable = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""} ${error?.hint || ""}`;
  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    message.includes(SALARY_APP_MESSAGE_OUTBOX_TABLE)
  );
};

const isEmptySalaryRow = (row) => {
  const config = salaryTypes[row.salary_type || "driver"];
  const editableFields = [...config.payment, ...config.deduction].filter((field) =>
    moneyInputFields.has(field)
  );
  const hasPersonInput =
    String(row.driver_name || "").trim() ||
    String(row.resident_number || "").trim() ||
    String(row.salary_pay_date || row.salary_pay_day || "").trim();
  return (
    !hasPersonInput &&
    editableFields.every((field) => toNumber(row[field]) === 0) &&
    !row.national_pension_exempt &&
    !row.employment_insurance_exempt
  );
};

export default function SalaryInput() {
  const params = new URLSearchParams(window.location.search);
  const [month, setMonth] = useState(getMonthStart(params.get("month")));
  const [salaryType, setSalaryType] = useState(params.get("type") || "driver");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seniorMode, setSeniorMode] = useState(false);
  const [detailIndex, setDetailIndex] = useState(null);
  const [selectedRows, setSelectedRows] = useState({});
  const [printType, setPrintType] = useState("driver");
  const [printSelectedRows, setPrintSelectedRows] = useState([]);
  const [noteTargetIndex, setNoteTargetIndex] = useState(null);
  const [appMessageDate, setAppMessageDate] = useState(getToday());
  const [appMessageTime, setAppMessageTime] = useState(getCurrentTime());
  const [appMessageSending, setAppMessageSending] = useState(false);
  const [appMessagedSalaryIds, setAppMessagedSalaryIds] = useState(new Set());

  const config = salaryTypes[salaryType] || salaryTypes.driver;
  const calculatedRows = useMemo(() => rows.map(calculateRow), [rows]);
  const totals = useMemo(() => {
    const fields = [
      ...config.payment,
      "payment_total",
      ...config.deduction,
      "deduction_total",
      "net_pay",
    ];
    return fields.reduce((acc, field) => {
      acc[field] = calculatedRows.reduce((sum, row) => sum + toNumber(row[field]), 0);
      return acc;
    }, {});
  }, [calculatedRows, config.deduction, config.payment]);
  const detailRow =
    detailIndex === null || detailIndex === undefined ? null : calculatedRows[detailIndex] || null;

  const resolveCompanyName = async (company) => {
    const { data: ownerProfile } = await supabase
      .from("profiles")
      .select("company_name")
      .eq("email", company)
      .maybeSingle();

    if (ownerProfile?.company_name) return ownerProfile.company_name;

    const { data: companyProfiles } = await supabase
      .from("profiles")
      .select("company_name")
      .eq("company", company)
      .not("company_name", "is", null)
      .limit(1);

    return companyProfiles?.[0]?.company_name || null;
  };

  const fetchSalaryRowExclusions = async (company, targetMonth, targetType) => {
    const { data, error } = await supabase
      .from(SALARY_ROW_EXCLUSIONS_TABLE)
      .select("person_key")
      .eq("company", company)
      .eq("month", targetMonth)
      .eq("salary_type", targetType);

    if (error) {
      if (isMissingSalaryExclusionsTable(error)) {
        console.warn("급여 행 삭제 표시 테이블이 아직 없습니다. SQL 마이그레이션 실행 후 영구 삭제 표시가 적용됩니다.");
        return [];
      }
      throw error;
    }

    return data || [];
  };

  const fetchSalaryAppMessagedIds = async (company, targetMonth, targetType) => {
    const { data, error } = await supabase
      .from(SALARY_APP_MESSAGE_OUTBOX_TABLE)
      .select("salary_id")
      .eq("company", company)
      .eq("salary_month", targetMonth)
      .eq("salary_type", targetType)
      .in("status", ["pending", "sent"])
      .not("salary_id", "is", null)
      .range(0, 4999);

    if (error) {
      if (isMissingSalaryAppMessageOutboxTable(error)) {
        console.warn("급여 앱 메시지 대기열 테이블이 아직 없습니다. SQL 마이그레이션 실행 후 파란색 표시가 적용됩니다.");
        return new Set();
      }
      throw error;
    }

    return new Set((data || []).map((row) => String(row.salary_id)).filter(Boolean));
  };

  const fetchRows = async (targetMonth = month, targetType = salaryType) => {
    setLoading(true);
    setDetailIndex(null);
    setSelectedRows({});
    setAppMessagedSalaryIds(new Set());
    try {
      const company = localStorage.getItem("email");
      const companyName = await resolveCompanyName(company);
      const targetConfig = salaryTypes[targetType] || salaryTypes.driver;
      const peopleRequest =
        targetType === "office"
          ? supabase
              .from("client")
              .select("id, name, phone, hire_date, position, branch, user_id, email, company")
              .eq("company", company)
              .order("id", { ascending: true })
          : supabase
              .from("profiles")
              .select(
                "id, role, hire_date, driver_name, phone, email, car_number, company, company_name, resident_number, salary_pay_day"
              )
              .eq("role", targetConfig.role)
              .eq("company", company)
              .order("hire_date", { ascending: true, nullsFirst: false })
              .order("driver_name", { ascending: true })
              .order("car_number", { ascending: true });

      const [
        { data: people, error: peopleError },
        { data: salaries, error: salaryError },
        exclusions,
        appMessageIds,
      ] =
        await Promise.all([
          peopleRequest,
          supabase
            .from("salary")
            .select("*")
            .eq("company", company)
            .eq("month", targetMonth)
            .eq("salary_type", targetType),
          fetchSalaryRowExclusions(company, targetMonth, targetType),
          fetchSalaryAppMessagedIds(company, targetMonth, targetType),
        ]);

      if (peopleError) throw peopleError;
      if (salaryError) throw salaryError;

      const salaryRows = salaries || [];
      const matchedSalaryIds = new Set();
      const exclusionKeys = new Set((exclusions || []).map((row) => row.person_key).filter(Boolean));
      const normalizedPeople = (people || []).map((person) =>
        targetType === "office"
          ? {
              id: person.id || null,
              profile_id: person.id || null,
              role: "office",
              driver_name: person.name || "",
              driver_email: person.email || "",
              email: person.email || "",
              phone: person.phone || null,
              car_number: "",
              company: person.company || company,
              company_name: companyName,
              hire_date: person.hire_date || "",
              resident_number: "",
              salary_pay_day: "",
            }
          : { ...person, profile_id: person.id || person.profile_id || null }
      ).filter((person) => !exclusionKeys.has(getSalaryPersonKey(person)));
      const nextRows = normalizedPeople.map((person) => {
        const found = salaryRows.find((salary) => {
          if (salary.id && matchedSalaryIds.has(salary.id)) return false;
          return isSameSalaryPerson(person, salary);
        });

        if (found?.id) matchedSalaryIds.add(found.id);
        return normalizeSalaryRow(person, found, targetMonth, company, companyName, targetType);
      });

      if (targetType === "daily") {
        salaryRows
          .filter((salary) => salary.id && !matchedSalaryIds.has(salary.id))
          .forEach((salary) => {
            nextRows.push(
              normalizeSalaryRow(
                {
                  id: salary.profile_id || null,
                  role: "daily",
                  driver_name: salary.driver_name || "",
                  email: salary.driver_email || "",
                  phone: salary.phone || "",
                  resident_number: salary.resident_number || "",
                  salary_pay_day: salary.salary_pay_day || "",
                },
                salary,
                targetMonth,
                company,
                companyName,
                targetType
              )
            );
          });

        nextRows.push(makeEmptySalary({ role: "daily" }, targetMonth, company, companyName, targetType));
      }

      setRows(nextRows);
      setAppMessagedSalaryIds(appMessageIds);
    } catch (error) {
      console.error("급여 내역 조회 실패:", error);
      showUserAlert("급여 내역 조회 실패: " + (error.message || JSON.stringify(error)));
      setRows([]);
      setAppMessagedSalaryIds(new Set());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(month, salaryType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTypeChange = (nextType) => {
    setSalaryType(nextType);
    setSeniorMode(false);
    setSelectedRows({});
    setPrintType(nextType);
    setPrintSelectedRows([]);
    fetchRows(month, nextType);
  };

  const findMatchingSalary = (salaryRows, row) =>
    salaryRows.find((salary) => {
      return isSameSalaryPerson(row, salary);
    });

  const handleCopyPreviousMonth = async () => {
    if (rows.length === 0) {
      showUserAlert("먼저 현재 급여월의 대상을 조회해 주세요.");
      return;
    }

    const previousMonth = getPreviousMonthStart(month);
    const company = localStorage.getItem("email");
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("salary")
        .select("*")
        .eq("company", company)
        .eq("month", previousMonth)
        .eq("salary_type", salaryType);

      if (error) throw error;

      const previousRows = data || [];
      if (previousRows.length === 0) {
        showUserAlert(`${previousMonth.slice(0, 7)} 급여 내역이 없습니다.`);
        return;
      }

      const copyFields = [
        "base_salary",
        "service_allowance",
        "extra_overtime_allowance",
        "allowance",
        "meal_allowance",
        "daily_wage",
        "work_days",
        "national_pension",
        "health_insurance",
        "care_insurance",
        "income_tax",
        "advance_payment",
        "year_end_adjustment",
        "advance_payment_note",
        "national_pension_exempt",
        "employment_insurance_exempt",
      ];

      let copiedCount = 0;
      const usedPreviousIds = new Set();
      const copyPreviousToRow = (row, previous) => {
        const next = { ...row };
        copyFields.forEach((field) => {
          if (field in previous) {
            next[field] =
              field === "national_pension_exempt" || field === "employment_insurance_exempt"
                ? Boolean(previous[field])
                : toNumber(previous[field]);
          }
        });
        return next;
      };

      const nextRows = rows.map((row) => {
        const previous = findMatchingSalary(previousRows, row);
        if (!previous) return row;
        if (previous.id) usedPreviousIds.add(previous.id);
        copiedCount += 1;
        return copyPreviousToRow(row, previous);
      });

      if (salaryType === "daily") {
        previousRows
          .filter((previous) => previous.id && !usedPreviousIds.has(previous.id))
          .forEach((previous) => {
            copiedCount += 1;
            nextRows.splice(
              Math.max(0, nextRows.length - 1),
              0,
              copyPreviousToRow(
                {
                  ...makeEmptySalary(
                    {
                      role: "daily",
                      driver_name: previous.driver_name || "",
                      email: previous.driver_email || "",
                      phone: previous.phone || "",
                      resident_number: previous.resident_number || "",
                      salary_pay_day: previous.salary_pay_day || "",
                    },
                    month,
                    company,
                    previous.company_name || null,
                    "daily"
                  ),
                  id: null,
                  profile_id: null,
                  driver_name: previous.driver_name || "",
                  driver_email: previous.driver_email || null,
                  phone: previous.phone || null,
                  resident_number: previous.resident_number || "",
                  salary_pay_day: previous.salary_pay_day || "",
                },
                previous
              )
            );
          });
      }

      setRows(nextRows);

      showUserAlert(`${previousMonth.slice(0, 7)} 내역 ${copiedCount}건을 현재 화면에 채웠습니다. 저장 버튼을 눌러야 DB에 반영됩니다.`);
    } catch (error) {
      console.error("지난 달 내역 복사 실패:", error);
      showUserAlert("지난 달 내역 복사 실패: " + (error.message || JSON.stringify(error)));
    } finally {
      setLoading(false);
    }
  };

  const updateField = (index, field, value) => {
    const nextValue = moneyInputFields.has(field) ? toNumber(value) : value;
    setRows((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        if (field === "salary_pay_date") {
          const day = nextValue ? Number(String(nextValue).slice(-2)) : "";
          return { ...row, salary_pay_date: nextValue, salary_pay_day: day || "" };
        }
        return { ...row, [field]: nextValue };
      })
    );
  };

  const toggleRowSelection = (index, checked) => {
    setSelectedRows((prev) => {
      const next = { ...prev };
      if (checked) next[index] = true;
      else delete next[index];
      return next;
    });
  };

  const toggleAllRows = (checked) => {
    if (!checked) {
      setSelectedRows({});
      return;
    }

    setSelectedRows(
      calculatedRows.reduce((acc, _, index) => {
        acc[index] = true;
        return acc;
      }, {})
    );
  };

  const toggleExemption = (index, field) => {
    if (!seniorMode) return;
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: !row[field] } : row
      )
    );
  };

  const buildPayload = (row) => {
    const calculated = calculateRow(row);
    return {
      profile_id: calculated.profile_id,
      salary_type: calculated.salary_type || salaryType,
      company: calculated.company,
      company_name: calculated.company_name,
      driver_name: calculated.driver_name,
      driver_email: calculated.driver_email,
      phone: calculated.phone,
      car_number: calculated.car_number,
      resident_number: calculated.resident_number,
      salary_pay_day: calculated.salary_pay_day ? Number(calculated.salary_pay_day) : null,
      salary_pay_date: calculated.salary_pay_date || null,
      month: calculated.month,
      salary: String(calculated.net_pay || 0),
      base_salary: calculated.base_salary,
      service_allowance: calculated.service_allowance,
      extra_overtime_allowance: calculated.extra_overtime_allowance,
      allowance: calculated.allowance,
      meal_allowance: calculated.meal_allowance,
      daily_wage: calculated.daily_wage,
      work_days: calculated.work_days,
      overtime_allowance: calculated.overtime_allowance,
      national_pension: String(calculated.national_pension || 0),
      health_insurance: String(calculated.health_insurance || 0),
      care_insurance: String(calculated.care_insurance || 0),
      employment_insurance: String(calculated.employment_insurance || 0),
      income_tax: String(calculated.income_tax || 0),
      local_income_tax: calculated.local_income_tax,
      advance_payment: calculated.advance_payment,
      advance_payment_note: calculated.advance_payment_note || "",
      year_end_adjustment: calculated.year_end_adjustment,
      other_deduction: calculated.other_deduction,
      payment_total: calculated.payment_total,
      deduction_total: calculated.deduction_total,
      net_pay: calculated.net_pay,
      national_pension_exempt: calculated.national_pension_exempt,
      employment_insurance_exempt: calculated.employment_insurance_exempt,
    };
  };

  const isMissingSalaryPayDateColumn = (error) => {
    const message = `${error?.message || ""} ${error?.details || ""} ${error?.hint || ""}`;
    return message.includes("salary_pay_date") && message.includes("schema cache");
  };

  const withoutSalaryPayDate = (payloads) =>
    payloads.map(({ salary_pay_date, ...payload }) => payload);

  const saveSalaryPayloads = async (items) => {
    const updateById = new Map();
    items
      .filter(({ row }) => row.id)
      .forEach(({ row, payload }) => {
        updateById.set(row.id, { id: row.id, ...payload });
      });
    const updates = Array.from(updateById.values());
    const inserts = items
      .filter(({ row }) => !row.id)
      .map(({ payload }) => payload);

    const runSave = async (payloads, mode) => {
      if (payloads.length === 0) return;
      const request =
        mode === "update"
          ? supabase.from("salary").upsert(payloads, { onConflict: "id" })
          : supabase.from("salary").insert(payloads);
      const { error } = await request;
      if (!error) return;
      if (!isMissingSalaryPayDateColumn(error)) throw error;

      const fallbackPayloads = withoutSalaryPayDate(payloads);
      const fallbackRequest =
        mode === "update"
          ? supabase.from("salary").upsert(fallbackPayloads, { onConflict: "id" })
          : supabase.from("salary").insert(fallbackPayloads);
      const { error: fallbackError } = await fallbackRequest;
      if (fallbackError) throw fallbackError;
    };

    await Promise.all([
      runSave(updates, "update"),
      runSave(inserts, "insert"),
    ]);
  };

  const expenseMemo = (type) =>
    type === "driver" ? "급여 내역 자동 등록" : `급여 내역 자동 등록 - ${salaryTypes[type]?.label || type}`;

  const buildExpensePayload = (row) => {
    const calculated = calculateRow(row);
    const deductionTotal = toNumber(row.deduction_total ?? calculated.deduction_total);
    const netPay = toNumber(row.net_pay ?? calculated.net_pay);
    const type = calculated.salary_type || salaryType;
    return {
      company: calculated.company,
      company_name: calculated.company_name || null,
      date: getSalaryExpenseDate(
        calculated.month,
        calculated.salary_pay_date,
        calculated.salary_pay_day
      ),
      car_number: type === "driver" ? calculated.car_number || "" : "",
      human_name: calculated.driver_name || "",
      phone: calculated.phone || null,
      johab: 0,
      unyoung: 0,
      gps: 0,
      halbu: 0,
      geubyeo: netPay,
      "4bohum": deductionTotal,
      tonghaeng: 0,
      joocha: 0,
      sooksic: 0,
      jungbi: 0,
      jooyu: 0,
      bohum: 0,
      secha: 0,
      chooljang: 0,
      gwatae: 0,
      gita: 0,
      bigo: expenseMemo(type),
    };
  };

  const normalizeExpenseText = (value) =>
    String(value || "").trim().replace(/\s+/g, " ");

  const getExpenseMatchKey = (row) =>
    [
      row.date || "",
      row.car_number || "",
      row.human_name || "",
      row.bigo || "",
    ].map(normalizeExpenseText).join("__");

  const getExpensePersonKey = (row) =>
    [
      row.date || "",
      row.human_name || "",
      row.bigo || "",
    ].map(normalizeExpenseText).join("__");

  const getExpensePhoneKey = (row) => {
    const phone = String(row.phone || "").replace(/\D/g, "");
    if (!phone) return "";
    return [
      row.date || "",
      phone,
      row.bigo || "",
    ].map((value) => String(value || "").trim()).join("__");
  };

  const addToGroupMap = (map, key, row) => {
    if (!key) return;
    const list = map.get(key) || [];
    list.push(row);
    map.set(key, list);
  };

  const syncExpensesForSalaries = async (items) => {
    const expensePayloadsRaw = items
      .map(({ row, payload }) => {
        const calculated = calculateRow({ ...row, ...(payload || {}) });
        if (!calculated.company) throw new Error(`${calculated.driver_name || "이름 없음"} 급여 지출 등록 실패: 회사 정보가 없습니다.`);
        if (!calculated.month) throw new Error(`${calculated.driver_name || "이름 없음"} 급여 지출 등록 실패: 급여월이 없습니다.`);
        if (!calculated.driver_name) throw new Error("급여 지출 등록 실패: 성명이 없는 행이 있습니다.");

        const expensePayload = buildExpensePayload(calculated);
        if (!expensePayload.date) throw new Error(`${calculated.driver_name} 급여 지출 등록 실패: 지출일자가 없습니다.`);
        if (toNumber(expensePayload.geubyeo) === 0 && toNumber(expensePayload["4bohum"]) === 0) return null;
        return expensePayload;
      })
      .filter(Boolean);

    const expensePayloads = Array.from(
      new Map(expensePayloadsRaw.map((payload) => [getExpenseMatchKey(payload), payload])).values()
    );

    if (expensePayloads.length === 0) return;

    const company = expensePayloads[0].company;
    const dates = expensePayloads.map((row) => row.date).filter(Boolean).sort();
    const fromDate = dates[0];
    const toDate = dates[dates.length - 1];
    const memoValues = Array.from(new Set(expensePayloads.map((row) => row.bigo).filter(Boolean)));

    const { data: existingRows, error: findError } = await supabase
      .from("cars-expenses")
      .select("id, date, car_number, human_name, phone, bigo")
      .eq("company", company)
      .gte("date", fromDate)
      .lte("date", toDate)
      .in("bigo", memoValues)
      .range(0, 4999);

    if (findError) throw findError;

    const existingByKey = new Map(
      (existingRows || []).map((row) => [getExpenseMatchKey(row), row])
    );
    const existingByPersonKey = new Map();
    const existingByPhoneKey = new Map();
    (existingRows || []).forEach((row) => {
      addToGroupMap(existingByPersonKey, getExpensePersonKey(row), row);
      addToGroupMap(existingByPhoneKey, getExpensePhoneKey(row), row);
    });

    const usedExistingIds = new Set();
    const deleteIds = new Set();
    const updates = [];
    const inserts = [];

    expensePayloads.forEach((payload) => {
      const exact = existingByKey.get(getExpenseMatchKey(payload));
      const samePhoneRows = existingByPhoneKey.get(getExpensePhoneKey(payload)) || [];
      const samePersonRows = existingByPersonKey.get(getExpensePersonKey(payload)) || [];
      const candidates = [
        exact,
        ...samePhoneRows,
        ...samePersonRows,
      ].filter((row, index, array) =>
        row?.id && array.findIndex((item) => item?.id === row.id) === index
      );

      const existing =
        candidates.find((row) => row.id === exact?.id) ||
        candidates.find((row) => !usedExistingIds.has(row.id) && !String(row.car_number || "").trim()) ||
        candidates.find((row) => !usedExistingIds.has(row.id)) ||
        null;

      if (existing?.id) {
        usedExistingIds.add(existing.id);
        updates.push({ id: existing.id, ...payload });
        candidates
          .filter((row) => row.id !== existing.id)
          .forEach((row) => deleteIds.add(row.id));
      } else {
        inserts.push(payload);
      }
    });

    const { error: updateError } =
      updates.length > 0
        ? await supabase.from("cars-expenses").upsert(updates, { onConflict: "id" })
        : { error: null };
    if (updateError) throw updateError;

    const { error: insertError } =
      inserts.length > 0
        ? await supabase.from("cars-expenses").insert(inserts)
        : { error: null };
    if (insertError) throw insertError;

    if (deleteIds.size > 0) {
      const { error: deleteError } = await supabase
        .from("cars-expenses")
        .delete()
        .eq("company", company)
        .in("id", Array.from(deleteIds));
      if (deleteError) throw deleteError;
    }
  };

  const deleteExpenseForSalary = async (row) => {
    const calculated = calculateRow(row);
    const type = calculated.salary_type || salaryType;
    if (!calculated.company || !calculated.month || !calculated.driver_name) return;

    const payload = buildExpensePayload(calculated);
    const targetKey = getExpenseMatchKey(payload);
    const targetPersonKey = getExpensePersonKey(payload);
    const targetPhoneKey = getExpensePhoneKey(payload);
    const { data: existingRows, error: findError } = await supabase
      .from("cars-expenses")
      .select("id, date, car_number, human_name, phone, bigo")
      .eq("company", calculated.company)
      .gte("date", getMonthStart(calculated.month))
      .lte("date", getMonthEnd(calculated.month))
      .eq("bigo", expenseMemo(type));

    if (findError) throw findError;

    const deleteIds = (existingRows || [])
      .filter((expense) => {
        if (getExpenseMatchKey(expense) === targetKey) return true;
        if (getExpensePersonKey(expense) === targetPersonKey) return true;
        return targetPhoneKey && getExpensePhoneKey(expense) === targetPhoneKey;
      })
      .map((expense) => expense.id)
      .filter(Boolean);

    if (deleteIds.length === 0) return;

    const { error } = await supabase
      .from("cars-expenses")
      .delete()
      .eq("company", calculated.company)
      .in("id", deleteIds);

    if (error) throw error;
  };

  const buildSalaryRowExclusion = (row, company) => {
    const calculated = calculateRow({
      ...row,
      company: row.company || company,
      month: row.month || month,
      salary_type: row.salary_type || salaryType,
    });
    const personKey = getSalaryPersonKey(calculated);
    if (!personKey) return null;

    return {
      company: calculated.company,
      company_name: calculated.company_name || localStorage.getItem("companyName") || null,
      month: calculated.month,
      salary_type: calculated.salary_type || salaryType,
      person_key: personKey,
      profile_id: calculated.profile_id || null,
      driver_name: calculated.driver_name || null,
      driver_email: calculated.driver_email || null,
      phone: calculated.phone || null,
      car_number: calculated.car_number || null,
      resident_number: calculated.resident_number || null,
    };
  };

  const saveSalaryRowExclusions = async (targetRows, company) => {
    if (salaryType === "daily") return;

    const payloads = Array.from(
      new Map(
        targetRows
          .map((row) => buildSalaryRowExclusion(row, company))
          .filter(Boolean)
          .map((row) => [row.person_key, row])
      ).values()
    );
    if (payloads.length === 0) return;

    const { error } = await supabase
      .from(SALARY_ROW_EXCLUSIONS_TABLE)
      .upsert(payloads, { onConflict: "company,month,salary_type,person_key" });

    if (error) {
      if (isMissingSalaryExclusionsTable(error)) {
        console.warn("급여 행 삭제 표시 테이블이 없어 현재 화면에서만 삭제합니다. 마이그레이션 실행 후 새로고침 뒤에도 유지됩니다.");
        return;
      }
      throw error;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const items = rows
        .filter((row) => row.id || !isEmptySalaryRow(row))
        .map((row) => ({
          row,
          payload: buildPayload(row),
        }));

      if (items.length > 0) {
        await saveSalaryPayloads(items);
        await syncExpensesForSalaries(items);
      }

      showUserAlert("급여 내역 저장 완료");
      await fetchRows(month, salaryType);
    } catch (error) {
      console.error("급여 내역 저장 실패:", error);
      showUserAlert("급여 내역 저장 실패: " + (error.message || JSON.stringify(error)));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSelectedRows = async () => {
    const selectedIndexes = Object.keys(selectedRows).map(Number).filter(Number.isFinite);
    if (selectedIndexes.length === 0) {
      showUserAlert("삭제할 급여 행을 선택해 주세요.");
      return;
    }

    if (!window.confirm(`선택한 ${selectedIndexes.length}개 행을 삭제할까요?`)) return;

    setSaving(true);
    try {
      const selectedIndexSet = new Set(selectedIndexes);
      const targetRows = rows.filter((_, index) => selectedIndexSet.has(index));
      const company = localStorage.getItem("email") || "";
      const { data: monthSalaryRows, error: monthSalaryError } = await supabase
        .from("salary")
        .select("*")
        .eq("company", company)
        .eq("month", month)
        .eq("salary_type", salaryType);

      if (monthSalaryError) throw monthSalaryError;

      const salaryRowsToDeleteById = new Map();
      await saveSalaryRowExclusions(targetRows, company);

      targetRows.forEach((row) => {
        if (row.id) salaryRowsToDeleteById.set(String(row.id), row);
        (monthSalaryRows || [])
          .filter((salary) => {
            if (row.id && salary.id && String(row.id) === String(salary.id)) return true;
            return isSameSalaryPerson(row, salary);
          })
          .forEach((salary) => {
            if (salary.id) salaryRowsToDeleteById.set(String(salary.id), salary);
          });
      });

      const salaryRowsToDelete = Array.from(salaryRowsToDeleteById.values());
      const deleteIds = Array.from(salaryRowsToDeleteById.keys());

      if (deleteIds.length > 0) {
        const { data: deletedRows, error } = await supabase
          .from("salary")
          .delete()
          .eq("company", company)
          .in("id", deleteIds)
          .select("id");
        if (error) throw error;

        if ((deletedRows || []).length === 0) {
          throw new Error("선택한 급여 행을 DB에서 삭제하지 못했습니다. 권한 또는 중복 데이터를 확인해 주세요.");
        }
      }

      const expenseDeleteTargets = [...targetRows, ...salaryRowsToDelete];
      const expenseDeleteKeys = new Set();
      for (const row of expenseDeleteTargets) {
        const key = [
          row.company || company,
          row.month || month,
          row.salary_type || salaryType,
          row.id || "",
          normalizePhoneKey(row.phone),
          normalizeEmailKey(row.driver_email || row.email),
          normalizeTextKey(row.driver_name),
          normalizeTextKey(row.car_number),
        ].join("__");
        if (expenseDeleteKeys.has(key)) continue;
        expenseDeleteKeys.add(key);
        await deleteExpenseForSalary({ ...row, company: row.company || company, month: row.month || month });
      }

      const remainingRows = rows.filter((_, index) => !selectedIndexSet.has(index));
      if (salaryType === "daily") {
        const hasBlankRow = remainingRows.some((row) => !row.id && isEmptySalaryRow(row));
        setRows(
          hasBlankRow
            ? remainingRows
            : [
                ...remainingRows,
                makeEmptySalary({ role: "daily" }, month, localStorage.getItem("email"), null, "daily"),
              ]
        );
      } else {
        setRows(remainingRows);
      }
      setSelectedRows({});
      showUserAlert("삭제되었습니다.");
    } catch (error) {
      console.error("급여 행 삭제 실패:", error);
      showUserAlert("삭제 실패: " + (error.message || JSON.stringify(error)));
    } finally {
      setSaving(false);
    }
  };

  const handlePrintDetail = () => {
    window.print();
  };

  const handlePrintSelectedRows = () => {
    const rowsToPrint = calculatedRows.filter((_, index) => selectedRows[index]);
    if (rowsToPrint.length === 0) {
      showUserAlert("인쇄할 행을 선택해 주세요.");
      return;
    }
    setPrintType(salaryType);
    setPrintSelectedRows(rowsToPrint);
    setTimeout(() => window.print(), 0);
  };

  const handleSendSalaryAppMessage = async () => {
    if (appMessageSending) return;
    if (selectedCount === 0) {
      showUserAlert("앱 메시지를 보낼 사람을 선택해 주세요.");
      return;
    }

    if (!appMessageDate || !appMessageTime) {
      showUserAlert("앱 메시지를 보낼 날짜와 시간을 입력해 주세요.");
      return;
    }

    const company = localStorage.getItem("email") || "";
    if (!company) {
      showUserAlert("회사 정보가 없습니다. 다시 로그인해 주세요.");
      return;
    }

    const selectedTargetRows = calculatedRows.filter((_, index) => selectedRows[index]);
    const targetRows = selectedTargetRows.filter(
      (row) =>
        row.id &&
        !isEmptySalaryRow(row) &&
        (normalizePhoneKey(row.phone) || normalizeEmailKey(row.driver_email || row.email))
    );

    if (targetRows.length === 0) {
      showUserAlert("선택한 행 중 앱 메시지를 보낼 저장된 급여 내역이 없습니다. 먼저 저장하거나 연락처를 확인해 주세요.");
      return;
    }

    const sendAt = `${appMessageDate}T${appMessageTime}:00+09:00`;
    const salaryMonthLabel = month.slice(0, 7);
    const message = `${salaryMonthLabel} 급여 내역이 등록되었습니다. 기사앱에서 확인해 주세요.`;

    const payloads = targetRows.map((row) => ({
      company,
      company_name: row.company_name || localStorage.getItem("companyName") || null,
      salary_id: row.id,
      salary_month: row.month || month,
      salary_type: row.salary_type || salaryType,
      driver_name: row.driver_name || "",
      driver_email: row.driver_email || row.email || null,
      phone: row.phone || null,
      car_number: row.car_number || null,
      send_at: sendAt,
      message,
      status: "pending",
    }));

    setAppMessageSending(true);
    try {
      const { error } = await supabase
        .from(SALARY_APP_MESSAGE_OUTBOX_TABLE)
        .upsert(payloads, { onConflict: "company,salary_id,send_at" });
      if (error) throw error;
      setAppMessagedSalaryIds((prev) => {
        const next = new Set(prev);
        targetRows.forEach((row) => {
          if (row.id) next.add(String(row.id));
        });
        return next;
      });
      showUserAlert(`${targetRows.length}건의 앱 급여 알림을 ${appMessageDate} ${appMessageTime}에 보내도록 등록했습니다.`);
    } catch (error) {
      console.error("급여 앱 메시지 등록 실패:", error);
      showUserAlert("앱 메시지 등록 실패: " + (error.message || JSON.stringify(error)));
    } finally {
      setAppMessageSending(false);
    }
  };

  const closeAdvanceNote = () => {
    setNoteTargetIndex(null);
  };

  const inputClass = "box-border min-w-[72px] w-full border px-1 py-1 text-right";
  const readonlyClass = "border px-1 py-1 text-right bg-gray-50";
  const selectedCount = Object.keys(selectedRows).length;
  const allRowsSelected = calculatedRows.length > 0 && calculatedRows.every((_, index) => selectedRows[index]);
  const printConfig = salaryTypes[printType] || salaryTypes.driver;
  const personnelColClass = (field) => {
    if (field === "hire_date") return "w-24";
    if (field === "driver_name") return "w-14";
    if (field === "car_number") return "w-16";
    if (field === "resident_number") return "w-32";
    return "w-20";
  };
  const totalColumns =
    1 +
    1 +
    config.personnel.length +
    config.payment.length +
    1 +
    config.deduction.length +
    1 +
    1 +
    1;

  const renderEditableCell = (row, index, field) => {
    if (salaryType === "driver" && field === "advance_payment") {
      return (
        <td key={field} className="relative border px-1 py-1 text-right">
          <input
            value={formatMoney(row.advance_payment)}
            onChange={(event) => updateField(index, "advance_payment", event.target.value)}
            className={inputClass}
          />
          {toNumber(row.advance_payment) > 0 && (
            <button
              type="button"
              aria-label="가불 메모"
              title={row.advance_payment_note || "가불 메모 입력"}
              onClick={() => setNoteTargetIndex(index)}
              className={`absolute right-1 top-1 h-2.5 w-2.5 rounded-full border border-white ${
                row.advance_payment_note ? "bg-red-500" : "bg-amber-400"
              }`}
            />
          )}
        </td>
      );
    }

    if (field === "employment_insurance") {
      return (
        <td
          key={field}
          className={`border px-1 py-1 text-right ${seniorMode ? "cursor-pointer bg-yellow-50" : ""}`}
          onClick={() => toggleExemption(index, "employment_insurance_exempt")}
          title={row.employment_insurance_exempt ? "고령자 제외 적용" : "지급합계의 0.9%, 10원 미만 절사"}
        >
          <div className={readonlyClass}>{formatMoney(row.employment_insurance)}</div>
          {row.employment_insurance_exempt && <div className="text-[11px] text-blue-600">고령자 제외</div>}
        </td>
      );
    }

    if (field === "local_income_tax") {
      return (
        <td key={field} className={readonlyClass}>
          {formatMoney(row.local_income_tax)}
        </td>
      );
    }

    if (field === "national_pension") {
      return (
        <td
          key={field}
          className={`border px-1 py-1 text-right ${seniorMode ? "cursor-pointer bg-yellow-50" : ""}`}
          onClick={() => toggleExemption(index, "national_pension_exempt")}
          title={row.national_pension_exempt ? "고령자 제외 적용" : ""}
        >
          <input
            value={formatMoney(row.national_pension)}
            onChange={(event) => updateField(index, "national_pension", event.target.value)}
            disabled={row.national_pension_exempt}
            className={`${inputClass} ${row.national_pension_exempt ? "bg-gray-100 text-gray-400" : ""}`}
          />
          {row.national_pension_exempt && <div className="text-[11px] text-blue-600">고령자 제외</div>}
        </td>
      );
    }

    return (
      <td key={field} className="border px-1 py-1 text-right">
        <input
          value={formatMoney(row[field])}
          onChange={(event) => updateField(index, field, event.target.value)}
          className={inputClass}
        />
      </td>
    );
  };

  const renderPersonnelCell = (row, index, field) => {
    const isEditableDailyField = salaryType === "daily" && ["driver_name", "resident_number"].includes(field);
    const dailyInputClass =
      field === "resident_number"
        ? "w-32 border px-1 py-1 text-center"
        : "w-14 border px-1 py-1 text-center";
    if (!isEditableDailyField) {
      return (
        <td key={field} className="border px-2 py-1 text-center">
          {field === "hire_date" ? (row.hire_date || "").slice(0, 10) : row[field] || ""}
        </td>
      );
    }

    return (
      <td key={field} className="border px-1 py-1 text-center">
        <input
          value={row[field] || ""}
          onChange={(event) => updateField(index, field, event.target.value)}
          className={dailyInputClass}
        />
      </td>
    );
  };

  const detailPaymentFields = [...config.payment, "payment_total"];
  const detailDeductionFields = [...config.deduction, "deduction_total"];

  return (
    <div className="space-y-1">
      <Header />
      <div className="px-4 pt-1 pb-4 space-y-3">
        <style>{`
          @media print {
            body * {
              visibility: hidden !important;
            }

            .salary-detail-print,
            .salary-detail-print *,
            .salary-selected-print,
            .salary-selected-print * {
              visibility: visible !important;
            }

            .salary-selected-print {
              display: block !important;
              position: fixed !important;
              inset: 0 !important;
              width: 100% !important;
              padding: 18px !important;
              background: white !important;
            }

            .salary-detail-print {
              position: fixed !important;
              inset: 0 !important;
              width: 100% !important;
              max-width: none !important;
              box-shadow: none !important;
              padding: 24px !important;
            }

            .salary-detail-no-print {
              display: none !important;
            }
          }
        `}</style>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold mr-3">급여 내역</h2>
          <label className="text-sm font-semibold">급여월</label>
          <input
            type="month"
            value={toMonthInput(month)}
            onChange={(event) => setMonth(getMonthStart(event.target.value))}
            className="border px-2 py-1"
          />
          <Button onClick={() => fetchRows(month, salaryType)} disabled={loading}>
            {loading ? "조회중..." : "검색"}
          </Button>
          <Button
            type="button"
            onClick={() => setSeniorMode((prev) => !prev)}
            className={seniorMode ? "bg-blue-700 text-white hover:bg-blue-800" : "bg-blue-600 text-white hover:bg-blue-700"}
          >
            고령자 선택
          </Button>
          <Button
            type="button"
            onClick={handleCopyPreviousMonth}
            disabled={loading}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
          >
            지난 달 내역 복사하기
          </Button>
          <div className="ml-2 flex items-center gap-3 text-sm">
            {Object.entries(salaryTypes).map(([key, type]) => (
              <label key={key} className="flex items-center gap-1">
                <input
                  type="radio"
                  name="salaryType"
                  value={key}
                  checked={salaryType === key}
                  onChange={() => handleTypeChange(key)}
                />
                {type.label}
              </label>
            ))}
          </div>
          <Button
            type="button"
            onClick={handlePrintSelectedRows}
            disabled={selectedCount === 0}
            className="bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50"
          >
            인쇄
          </Button>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={appMessageDate}
              onChange={(event) => setAppMessageDate(event.target.value)}
              className="h-9 rounded border px-2 text-sm"
              disabled={appMessageSending}
            />
            <input
              type="time"
              value={appMessageTime}
              onChange={(event) => setAppMessageTime(event.target.value)}
              className="h-9 rounded border px-2 text-sm"
              disabled={appMessageSending}
            />
            <Button
              type="button"
              onClick={handleSendSalaryAppMessage}
              disabled={loading || saving || appMessageSending || selectedCount === 0}
              className="bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {appMessageSending ? "등록중..." : "앱 메시지 보내기"}
            </Button>
          </div>
        </div>

        {seniorMode && (
          <div className="text-sm text-blue-700">
            고령자 선택 중입니다. 국민연금 또는 고용보험 칸을 클릭하면 해당 공제가 0원으로 저장됩니다.
          </div>
        )}

        <div className="overflow-auto max-h-[72vh]">
          <table className="salary-ledger-table table-fixed w-full border border-gray-300 text-xs whitespace-nowrap">
            <colgroup>
              <col className="w-8" />
              <col className="w-8" />
              {config.personnel.map((field) => (
                <col
                  key={`personnel-col-${field}`}
                  className={personnelColClass(field)}
                />
              ))}
              {[...config.payment, "payment_total", ...config.deduction, "deduction_total", "net_pay"].map((field) => (
                <col key={`money-col-${field}`} className="w-20" />
              ))}
              <col className="w-32" />
            </colgroup>
            <thead className="sticky top-0 z-30 bg-blue-500 text-white">
              <tr>
                <th className="border px-2 py-1 text-center" colSpan={1 + 1 + config.personnel.length}>인적사항</th>
                <th className="border px-2 py-1 text-center" colSpan={config.payment.length + 1}>지급 내역</th>
                <th className="border px-2 py-1 text-center" colSpan={config.deduction.length + 1}>공제 내역</th>
                <th className="border px-2 py-1 text-center" rowSpan={2}>차인지급액</th>
                <th className="border px-2 py-1 text-center" rowSpan={2}>지급일</th>
              </tr>
              <tr>
                <th className="border px-1 py-1 text-center">
                  <label className="flex items-center justify-center gap-1" title="전체 선택">
                    <input
                      type="checkbox"
                      aria-label="전체 선택"
                      checked={allRowsSelected}
                      onChange={(event) => toggleAllRows(event.target.checked)}
                    />
                  </label>
                </th>
                <th className="border px-1 py-1 text-center">순번</th>
                {config.personnel.map((field) => (
                  <th key={field} className="border px-2 py-1 text-center">{fieldLabels[field]}</th>
                ))}
                {[...config.payment, "payment_total"].map((field) => (
                  <th key={field} className="border px-1 py-1 text-center">{fieldLabels[field]}</th>
                ))}
                {[...config.deduction, "deduction_total"].map((field) => (
                  <th key={field} className="border px-1 py-1 text-center">{fieldLabels[field]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {calculatedRows.length === 0 && !loading && (
                <tr>
                  <td className="border p-4 text-center text-gray-500" colSpan={totalColumns}>
                    데이터가 없습니다.
                  </td>
                </tr>
              )}

              {calculatedRows.map((row, index) => {
                const isAppMessaged = row.id && appMessagedSalaryIds.has(String(row.id));
                return (
                <tr
                  key={`${row.id || row.profile_id || row.phone || row.driver_email || "salary-row"}-${index}`}
                  className={isAppMessaged ? "bg-blue-100 hover:bg-blue-100" : "hover:bg-gray-50"}
                >
                  <td className="border px-1 py-1 text-center">
                    <input
                      type="checkbox"
                      checked={!!selectedRows[index]}
                      onChange={(event) => toggleRowSelection(index, event.target.checked)}
                    />
                  </td>
                  <td className="border px-1 py-1 text-center">
                    <button type="button" className="text-blue-600 underline" onClick={() => setDetailIndex(index)}>
                      {index + 1}
                    </button>
                  </td>
                  {config.personnel.map((field) => renderPersonnelCell(row, index, field))}
                  {config.payment.map((field) => renderEditableCell(row, index, field))}
                  <td className={readonlyClass}>{formatMoney(row.payment_total)}</td>
                  {config.deduction.map((field) => renderEditableCell(row, index, field))}
                  <td className={readonlyClass}>{formatMoney(row.deduction_total)}</td>
                  <td className="border px-1 py-1 text-right font-semibold bg-gray-50">{formatMoney(row.net_pay)}</td>
                  <td className="border px-1 py-1 text-center">
                    <input
                      type="date"
                      value={row.salary_pay_date || ""}
                      onChange={(event) => updateField(index, "salary_pay_date", event.target.value)}
                      className="h-7 w-32 rounded border bg-white px-1"
                    />
                  </td>
                </tr>
                );
              })}
              {calculatedRows.length > 0 && (
                <tr className="bg-gray-100 font-semibold">
                  <td className="border px-2 py-1 text-center" colSpan={1 + 1 + config.personnel.length}>
                    합계
                  </td>
                  {config.payment.map((field) => (
                    <td key={`total-${field}`} className="border px-1 py-1 text-right">
                      {formatMoney(totals[field])}
                    </td>
                  ))}
                  <td className="border px-1 py-1 text-right">{formatMoney(totals.payment_total)}</td>
                  {config.deduction.map((field) => (
                    <td key={`total-${field}`} className="border px-1 py-1 text-right">
                      {formatMoney(totals[field])}
                    </td>
                  ))}
                  <td className="border px-1 py-1 text-right">{formatMoney(totals.deduction_total)}</td>
                  <td className="border px-1 py-1 text-right">{formatMoney(totals.net_pay)}</td>
                  <td className="border px-1 py-1 text-center"></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            onClick={handleDeleteSelectedRows}
            disabled={saving || loading || selectedCount === 0}
            className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            삭제
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? "저장중..." : "저장"}
          </Button>
        </div>
      </div>

      <div className="salary-selected-print hidden">
        <h2 className="mb-3 text-lg font-semibold">
          {printConfig.label} 급여 내역 ({month.slice(0, 7)})
        </h2>
        <table className="w-full border-collapse border border-gray-300 text-xs">
          <thead>
            <tr className="bg-blue-500 text-white">
              <th className="border px-2 py-1 text-center" colSpan={printConfig.personnel.length}>인적사항</th>
              <th className="border px-2 py-1 text-center" colSpan={printConfig.payment.length + 1}>지급 내역</th>
              <th className="border px-2 py-1 text-center" colSpan={printConfig.deduction.length + 1}>공제 내역</th>
              <th className="border px-2 py-1 text-center" rowSpan={2}>차인지급액</th>
            </tr>
            <tr className="bg-blue-500 text-white">
              {printConfig.personnel.map((field) => (
                <th key={`print-head-${field}`} className="border px-2 py-1 text-center">{fieldLabels[field]}</th>
              ))}
              {[...printConfig.payment, "payment_total"].map((field) => (
                <th key={`print-head-${field}`} className="border px-2 py-1 text-center">{fieldLabels[field]}</th>
              ))}
              {[...printConfig.deduction, "deduction_total"].map((field) => (
                <th key={`print-head-${field}`} className="border px-2 py-1 text-center">{fieldLabels[field]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {printSelectedRows.map((row, index) => (
              <tr key={`print-salary-${row.id || row.profile_id || index}`}>
                {printConfig.personnel.map((field) => (
                  <td key={`print-${index}-${field}`} className="border px-2 py-1 text-center">
                    {field === "hire_date" ? (row.hire_date || "").slice(0, 10) : row[field] || ""}
                  </td>
                ))}
                {[...printConfig.payment, "payment_total"].map((field) => (
                  <td key={`print-${index}-${field}`} className="border px-2 py-1 text-right">
                    {formatMoney(row[field])}
                  </td>
                ))}
                {[...printConfig.deduction, "deduction_total"].map((field) => (
                  <td key={`print-${index}-${field}`} className="border px-2 py-1 text-right">
                    {formatMoney(row[field])}
                  </td>
                ))}
                <td className="border px-2 py-1 text-right">{formatMoney(row.net_pay)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detailRow && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4">
          <div className="salary-detail-print w-full max-w-3xl rounded bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {detailRow.driver_name || config.label} 급여 내역
              </h3>
              <div className="salary-detail-no-print flex items-center gap-2">
                <button type="button" className="rounded border px-3 py-1 text-sm hover:bg-gray-50" onClick={handlePrintDetail}>
                  인쇄
                </button>
                <button type="button" className="rounded border px-3 py-1 text-sm hover:bg-gray-50" onClick={() => setDetailIndex(null)}>
                  닫기
                </button>
              </div>
            </div>

            <section className="mb-4">
              <div className="mb-2 border-b pb-1 font-semibold">인적 사항</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <DetailField label="급여월" value={detailRow.month} />
                {config.personnel.map((field) => (
                  <DetailField
                    key={field}
                    label={fieldLabels[field]}
                    value={field === "hire_date" ? (detailRow.hire_date || "").slice(0, 10) : detailRow[field]}
                  />
                ))}
                <DetailField label="지급일" value={detailRow.salary_pay_date || ""} />
              </div>
            </section>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <section>
                <div className="mb-2 border-b pb-1 font-semibold">지급 내역</div>
                <div className="text-sm">
                  {detailPaymentFields.map((field) => (
                    <DetailField key={field} label={fieldLabels[field]} value={formatMoney(detailRow[field])} align="right" />
                  ))}
                </div>
              </section>

              <section>
                <div className="mb-2 border-b pb-1 font-semibold">공제 내역</div>
                <div className="text-sm">
                  {detailDeductionFields.map((field) => (
                    <React.Fragment key={field}>
                      <DetailField
                        label={fieldLabels[field]}
                        value={`${formatMoney(detailRow[field])}${
                          field === "national_pension" && detailRow.national_pension_exempt
                            ? " (고령자 제외)"
                            : field === "employment_insurance" && detailRow.employment_insurance_exempt
                              ? " (고령자 제외)"
                              : ""
                        }`}
                        align="right"
                      />
                      {field === "advance_payment" && detailRow.advance_payment_note && (
                        <DetailField label="가불 메모" value={detailRow.advance_payment_note} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </section>
            </div>

            <section className="mt-4 rounded bg-gray-50 p-3">
              <div className="flex items-center justify-between text-base font-semibold">
                <span>차인지급액</span>
                <span>{formatMoney(detailRow.net_pay)}</span>
              </div>
            </section>
          </div>
        </div>
      )}

      {noteTargetIndex !== null && calculatedRows[noteTargetIndex] && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold">
                {calculatedRows[noteTargetIndex].driver_name || "기사"} 가불 메모
              </h3>
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm hover:bg-gray-50"
                onClick={closeAdvanceNote}
              >
                닫기
              </button>
            </div>
            <textarea
              value={rows[noteTargetIndex]?.advance_payment_note || ""}
              onChange={(event) => updateField(noteTargetIndex, "advance_payment_note", event.target.value)}
              className="min-h-32 w-full rounded border p-2 text-sm"
              placeholder="예: 과태료 6/22&#10;택시비 7/3"
            />
            <div className="mt-3 flex justify-end">
              <Button type="button" onClick={closeAdvanceNote}>
                완료
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
